import { useState } from 'react';
import { formatMoney, formatDate } from '../lib/money';
import { daysOverdue } from '../lib/aging';
import { priorityDot } from '../lib/priority';
import { getOutstanding, getDisplayStatus } from '../lib/invoiceCalc';
import PoCard from './PoCard';

function InvoiceRow({ inv, mode, outstandingFor, paidFor, hasVariance, actions, firstAttachment }) {
  const isPaidMode = mode === 'paid';
  const paid = paidFor(inv.id);
  const outstanding = getOutstanding(inv, paid);
  const displayStatus = getDisplayStatus(inv, paid);
  const dotClass = priorityDot({ ...inv, status: displayStatus }, isPaidMode);
  const statusLabel = displayStatus.replace(/_/g, ' ');
  const days = displayStatus === 'paid' ? null : daysOverdue(inv.due_date);
  const agingText = days == null ? '—' : days === 0 ? '0' : days > 0 ? `-${days}` : `${Math.abs(days)}`;
  const agingColor = days == null ? 'var(--text-disabled)' : days > 0 ? 'var(--text-error)' : '#036e43';

  const handleActions = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    actions.onActions(inv, rect);
  };

  return (
    <div className={`invoice-row row-status-${displayStatus}`} onClick={() => actions.onOpenDrawer(inv)}>
      <div className="invoice-cell" data-col="dot">
        {firstAttachment ? (
          <button type="button" className={`row-eye-btn dot-color-${dotClass}`} onClick={e => { e.stopPropagation(); actions.onPreviewFile?.(firstAttachment); }} title="Preview attachment">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.5"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/></svg>
          </button>
        ) : (
          <span className={`priority-dot dot-${dotClass}`} aria-hidden="true" />
        )}
      </div>
      <div className="invoice-cell invoice-number" data-col="invoice">
        {inv.invoice_number || 'N/A'}
        {hasVariance(inv.id) && <span className="variance-badge">Variance</span>}
      </div>
      <div className="invoice-cell cell-muted" data-col="ps" title={inv.packing_slip_number || ''}>{inv.packing_slip_number || '—'}</div>
      <div className="invoice-cell cell-muted" data-col="tr" title={inv.tr_number || ''}>{inv.tr_number || '—'}</div>
      <div className="invoice-cell invoice-date" data-col="inv-date">{formatDate(inv.invoice_date)}</div>
      <div className="invoice-cell" data-col="aging" style={{ color: agingColor, fontVariantNumeric: 'tabular-nums', fontSize: 14 }}>
        {agingText}
      </div>
      <div className="invoice-cell cell-muted cell-right" data-col="total">{formatMoney(inv.total || 0)}</div>
      <div className="invoice-cell cell-muted cell-right" data-col="paid">{formatMoney(paid)}</div>
      <div className="invoice-cell cell-right cell-hero" data-col="approved">{inv.amount_approved ? formatMoney(inv.amount_approved) : '—'}</div>
      <div className="invoice-cell" data-col="status"><span className={`status-pill ${displayStatus}`}>{statusLabel}</span>{displayStatus === 'disputed' && inv.dispute_reason && <span className="dispute-reason-hint" title={inv.dispute_reason}>?</span>}</div>
      <div className="invoice-cell" data-col="notes">
        <button type="button" className="notes-icon-btn" onClick={e => { e.stopPropagation(); actions.onOpenActivity?.(inv); }} title="Activity">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 2.5A1.5 1.5 0 013.5 1h9A1.5 1.5 0 0114 2.5v9a1.5 1.5 0 01-1.5 1.5H5.5L2 16V2.5z" stroke="currentColor" strokeWidth="1.5" fill="none" /></svg>
        </button>
      </div>
      <div className="invoice-cell" data-col="actions">
        <button type="button" className="row-actions-btn" onClick={handleActions} aria-haspopup="menu">
          <span>Actions</span>
          <span className="caret">▸</span>
        </button>
      </div>
    </div>
  );
}

function PoSection({ po, invoices, mode, helpers, actions, invoiceAttachments, poAttachments }) {
  const { outstandingFor, paidFor, hasVariance } = helpers;
  const [showPaid, setShowPaid] = useState(false);

  const active = invoices.filter(inv => getDisplayStatus(inv, paidFor(inv.id)) !== 'paid');
  const paid = invoices.filter(inv => getDisplayStatus(inv, paidFor(inv.id)) === 'paid');

  const renderRow = (inv) => {
    const firstAtt = invoiceAttachments.find(a => a.invoice_id === inv.id) || null;
    return (
      <InvoiceRow
        key={inv.id} inv={inv} mode={mode}
        outstandingFor={outstandingFor} paidFor={paidFor}
        hasVariance={hasVariance} actions={actions}
        firstAttachment={firstAtt}
      />
    );
  };

  return (
    <PoCard
      po={po} invoices={invoices} helpers={helpers} actions={actions}
      firstPoAttachment={poAttachments.find(a => a.po_id === po?.id) || null}
    >
      {active.length > 0 && (
        <div className="invoice-rows">{active.map(renderRow)}</div>
      )}
      {paid.length > 0 && (
        <>
          <button type="button" className="paid-toggle" onClick={() => setShowPaid(s => !s)}>
            <span className={`expander ${showPaid ? '' : 'rotated'}`} style={{ fontSize: 10 }}>▾</span>
            {paid.length} paid invoice{paid.length === 1 ? '' : 's'}
          </button>
          {showPaid && <div className="invoice-rows">{paid.map(renderRow)}</div>}
        </>
      )}
    </PoCard>
  );
}

// Numeric value of a PO number for sorting — "PO-0483" → 483. Groups with no
// resolved PO sort last in both directions rather than flipping to the top.
function poSortValue(group) {
  const digits = (group.po?.po_number || '').replace(/\D/g, '');
  return digits ? parseInt(digits, 10) : null;
}

export default function InvoiceList({ invoices, pos, orphanPos = [], mode, outstandingFor, paidFor, hasVariance, actions, invoiceAttachments = [], poAttachments = [], sortAsc = true }) {
  const hasAnything = invoices.length > 0 || orphanPos.length > 0;
  if (!hasAnything) {
    return (
      <div className="po-table">
        <div className="empty-state">
          <div className="empty-state-title">No purchase orders yet</div>
        </div>
      </div>
    );
  }

  // One flat set of PO groups across every supplier. Previously these were
  // bucketed by supplier name and only ordered by PO number *inside* each
  // bucket, so the list read as unsorted (PO-0493, PO-0483, PO-0494...) —
  // supplier was the real sort key. PO number is now the only ordering key,
  // which is what the ASC/DESC toggle drives. (2026-07-23.)
  const poGroups = {};
  invoices.forEach(inv => {
    const poId = inv.po_id || 'unassigned';
    (poGroups[poId] = poGroups[poId] || { po: null, invoices: [] }).invoices.push(inv);
  });
  for (const [poId, group] of Object.entries(poGroups)) {
    group.po = (pos || []).find(p => p.id === poId) || null;
  }
  orphanPos.forEach(po => {
    if (!poGroups[po.id]) poGroups[po.id] = { po, invoices: [] };
  });

  const sorted = Object.values(poGroups).sort((a, b) => {
    const aNum = poSortValue(a);
    const bNum = poSortValue(b);
    if (aNum == null && bNum == null) return 0;
    if (aNum == null) return 1;
    if (bNum == null) return -1;
    return sortAsc ? aNum - bNum : bNum - aNum;
  });

  const helpers = { outstandingFor, paidFor, hasVariance };

  return (
    <div className="po-table">
      {sorted.map(group => (
        <PoSection
          key={group.po?.id || 'unassigned'}
          po={group.po}
          invoices={group.invoices}
          mode={mode}
          helpers={helpers}
          actions={actions}
          invoiceAttachments={invoiceAttachments}
          poAttachments={poAttachments}
        />
      ))}
    </div>
  );
}
